import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Gauge,
  Droplets,
  Zap,
  Flame,
  Radio,
  Wifi,
  Server,
  Shield,
  CheckCircle,
  ArrowRight,
  Building2,
  Factory,
  Globe,
  Clock,
  TrendingUp,
  AlertTriangle,
  BarChart3,
  Settings,
  Database,
  Lock,
  Signal,
  Landmark,
  Home
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/Header';
import { TopBar } from '@/components/TopBar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/sections/ContactForm';
import { HreflangTags } from '@/components/HreflangTags';
import { useLanguage } from '@/i18n/LanguageContext';

// Custom generated images
const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/92de248681a88b6565db7555630d66d3c4f09f41fd9c6711cd910b1463f3bffa.png",
  underground: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/78ebed0cd929ad8ba8305ac07a794791b356673e7c1e7765cf55ef3b33601af6.png",
  carrierGrade: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/7d4e3faa37d693833fece6d1baa0731d8ed5ea2de6edaee22a360a6914f433b4.png",
  cityDeployment: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/e1256a8a5f48d865549e9e2b6e910551b2a986896225ae62c5188ee11eaa751a.png",
  dashboard: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/c129726c4952c373ae9669eda4012a577e9e7073f3b0acb3bde48ef914e6ce87.png"
};

const SmartMeteringPage = () => {
  const { t } = useLanguage();
  
  return (
    <>
      <Helmet>
        <title>{t('smartMeteringPage.metaTitle')}</title>
        <meta name="description" content={t('smartMeteringPage.metaDesc')} />
        <meta name="keywords" content="smart metering, smart water metering, smart energy metering, IoT metering infrastructure, LoRaWAN smart metering, NB-IoT smart metering, carrier grade IoT network, utility IoT network, AMI infrastructure, smart utility networks" />
        <link rel="canonical" href="https://0gbaltics.com/smart-metering" />
        <meta property="og:title" content={t('smartMeteringPage.metaTitle')} />
        <meta property="og:description" content={t('smartMeteringPage.metaDesc')} />
        <meta property="og:type" content="website" />
      </Helmet>
      <HreflangTags />

      <TopBar />
      <Header />

      <main className="pt-20">
        
        {/* ========================================= */}
        {/* HERO SECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="smart-metering-hero">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('smartMeteringPage.heroBadge')}
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  {t('smartMeteringPage.heroTitle')}
                </h1>
                <p className="text-xl text-slate-300 mb-6 leading-relaxed">
                  {t('smartMeteringPage.heroSubtitle')}
                </p>
                <div className="flex flex-wrap gap-4 mb-8">
                  <Button 
                    size="lg"
                    className="bg-teal-500 hover:bg-teal-600 text-white"
                    onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                    data-testid="hero-cta"
                  >
                    {t('smartMeteringPage.ctaPrimary')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
                
                {/* Technology badges */}
                <div className="flex flex-wrap gap-3">
                  {['LoRaWAN', 'NB-IoT', 'LTE-M', 'Water', 'Energy', 'Gas'].map((tech, i) => (
                    <span key={i} className="bg-slate-800 border border-slate-700 text-slate-300 text-sm px-3 py-1 rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src={IMAGES.hero}
                  alt="Smart Metering Infrastructure - meters, gateways, cloud and utility dashboard"
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHAT IS SMART METERING */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="what-is-metering">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('smartMeteringPage.challengeBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('smartMeteringPage.challengeTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('smartMeteringPage.challengeDesc')}
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: BarChart3, title: t('smartMeteringPage.challenge1'), desc: t('smartMeteringPage.challenge1Desc') },
                    { icon: TrendingUp, title: t('smartMeteringPage.challenge2'), desc: t('smartMeteringPage.challenge2Desc') },
                    { icon: Clock, title: t('smartMeteringPage.challenge3'), desc: t('smartMeteringPage.challenge3Desc') },
                    { icon: AlertTriangle, title: t('smartMeteringPage.challenge4'), desc: t('smartMeteringPage.challenge4Desc') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="p-4 bg-slate-50 rounded-lg">
                        <Icon className="h-6 w-6 text-teal-600 mb-2" />
                        <h4 className="font-semibold text-slate-900">{item.title}</h4>
                        <p className="text-sm text-slate-600">{item.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="bg-slate-50 p-8 rounded-lg">
                <img 
                  src={IMAGES.dashboard}
                  alt="Utility operations dashboard showing smart metering data"
                  className="w-full h-auto rounded-lg mb-6"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHY NETWORK QUALITY MATTERS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="network-reliability">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('smartMeteringPage.networkBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('smartMeteringPage.networkTitle')}
                </h2>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  {t('smartMeteringPage.networkDesc')}
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {[
                    { icon: Home, label: t('smartMeteringPage.networkLocation1') },
                    { icon: Building2, label: t('smartMeteringPage.networkLocation2') },
                    { icon: Factory, label: t('smartMeteringPage.networkLocation3') },
                    { icon: Globe, label: t('smartMeteringPage.networkLocation4') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="bg-slate-800 p-4 rounded-lg flex items-center gap-3">
                        <Icon className="h-5 w-5 text-red-400" />
                        <span className="text-slate-300">{item.label}</span>
                      </div>
                    );
                  })}
                </div>
                
                <div className="bg-red-900/30 border border-red-700/50 p-4 rounded-lg">
                  <p className="text-red-300 text-sm">
                    {t('smartMeteringPage.networkWarning')}
                  </p>
                </div>
              </div>
              
              <div>
                <img 
                  src={IMAGES.underground}
                  alt="Meters in challenging locations - basements, buildings, underground pits"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHAT IS CARRIER GRADE */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="carrier-grade">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('smartMeteringPage.carrierBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('smartMeteringPage.carrierTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('smartMeteringPage.carrierDesc')}
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <img 
                  src={IMAGES.carrierGrade}
                  alt="Consumer IoT network vs Carrier-grade IoT infrastructure comparison"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
              
              <div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { title: t('smartMeteringPage.carrierFeature1'), desc: t('smartMeteringPage.carrierFeature1Desc') },
                    { title: t('smartMeteringPage.carrierFeature2'), desc: t('smartMeteringPage.carrierFeature2Desc') },
                    { title: t('smartMeteringPage.carrierFeature3'), desc: t('smartMeteringPage.carrierFeature3Desc') },
                    { title: t('smartMeteringPage.carrierFeature4'), desc: t('smartMeteringPage.carrierFeature4Desc') },
                  ].map((item, i) => (
                    <div key={i} className="bg-slate-50 p-4 rounded-lg">
                      <CheckCircle className="h-6 w-6 text-teal-600 mb-2" />
                      <h4 className="font-semibold text-slate-900">{item.title}</h4>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* LORAWAN FOR SMART METERING */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="lorawan-metering">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('smartMeteringPage.lorawanSectionBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('smartMeteringPage.lorawanSectionTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('smartMeteringPage.lorawanSectionDesc')}
                </p>
                
                <div className="space-y-3 mb-8">
                  {[
                    t('smartMeteringPage.lorawanAdvantage1'),
                    t('smartMeteringPage.lorawanAdvantage2'),
                    t('smartMeteringPage.lorawanAdvantage3'),
                    t('smartMeteringPage.lorawanAdvantage4'),
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white border border-slate-200 p-3 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-teal-600" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <img 
                  src={IMAGES.cityDeployment}
                  alt="LoRaWAN smart metering city deployment with gateways and meters"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* NB-IOT AND CELLULAR */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="cellular-metering">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('smartMeteringPage.cellularSectionBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('smartMeteringPage.cellularSectionTitle')}
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {t('smartMeteringPage.cellularSectionDesc')}
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-12">
                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                    <Wifi className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{t('smartMeteringPage.nbiotTitle')}</h3>
                  <p className="text-slate-600 mb-4">
                    {t('smartMeteringPage.nbiotDesc')}
                  </p>
                </div>
                
                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                    <Radio className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{t('smartMeteringPage.cellularTitle')}</h3>
                  <p className="text-slate-600 mb-4">
                    {t('smartMeteringPage.cellularDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* MULTI CONNECTIVITY */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="multi-connectivity">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                Hybrid Architecture
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                Multi-Connectivity Smart Metering
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                The most reliable smart metering infrastructures use <strong className="text-white">multiple connectivity technologies</strong>. This hybrid approach improves reliability and ensures continuous connectivity even in complex environments.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6 mb-12">
              {[
                { icon: Radio, name: 'LoRaWAN', desc: 'Primary network for most meters', color: 'bg-blue-600' },
                { icon: Wifi, name: 'NB-IoT', desc: 'Cellular backup and remote areas', color: 'bg-green-600' },
                { icon: Signal, name: 'LTE-M', desc: 'Mobile and high-bandwidth needs', color: 'bg-purple-600' },
                { icon: Database, name: 'BLE Gateways', desc: 'Indoor and last-mile connectivity', color: 'bg-orange-500' },
              ].map((tech, i) => {
                const Icon = tech.icon;
                return (
                  <div key={i} className="bg-slate-800 border border-slate-700 p-6 rounded-lg text-center">
                    <div className={`w-12 h-12 ${tech.color} rounded-lg flex items-center justify-center mx-auto mb-4`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="text-white font-semibold mb-2">{tech.name}</h4>
                    <p className="text-slate-400 text-sm">{tech.desc}</p>
                  </div>
                );
              })}
            </div>

            <div className="bg-slate-800 border border-slate-700 p-6 rounded-lg max-w-3xl mx-auto">
              <p className="text-slate-300 text-center">
                Multi-connectivity architectures provide <strong className="text-white">redundancy</strong> and <strong className="text-white">operational flexibility</strong>, ensuring that metering data is always collected regardless of individual network conditions.
              </p>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* METERING USE CASES */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="metering-use-cases">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('smartMeteringPage.useCasesBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('smartMeteringPage.useCasesTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('smartMeteringPage.useCasesDesc') || 'IoT-enabled smart metering transforms utility operations across water, energy, and gas distribution networks.'}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Water Metering */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden" data-testid="use-case-water">
                <div className="bg-blue-600 p-6">
                  <Droplets className="h-10 w-10 text-white mb-4" />
                  <h3 className="text-2xl font-semibold text-white">{t('smartMeteringPage.waterMetering')}</h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 mb-6">
                    {t('smartMeteringPage.waterMeteringDesc')}
                  </p>
                </div>
              </div>

              {/* Energy Metering */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden" data-testid="use-case-energy">
                <div className="bg-amber-500 p-6">
                  <Zap className="h-10 w-10 text-white mb-4" />
                  <h3 className="text-2xl font-semibold text-white">{t('smartMeteringPage.electricityMetering')}</h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 mb-6">
                    {t('smartMeteringPage.electricityMeteringDesc')}
                  </p>
                </div>
              </div>

              {/* Gas Metering */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden" data-testid="use-case-gas">
                <div className="bg-red-500 p-6">
                  <Flame className="h-10 w-10 text-white mb-4" />
                  <h3 className="text-2xl font-semibold text-white">{t('smartMeteringPage.gasMetering')}</h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 mb-6">
                    {t('smartMeteringPage.gasMeteringDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* INFRASTRUCTURE FOR CITIES */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="cities-infrastructure">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  Municipal Solutions
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  Smart Metering for Cities and Municipalities
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Cities increasingly deploy smart metering infrastructure as part of broader smart city initiatives. Municipal utilities benefit from unified IoT networks that support multiple applications beyond metering.
                </p>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  Reliable IoT connectivity is essential to ensure long-term project success. Municipal deployments often span decades, requiring infrastructure designed for longevity and adaptability.
                </p>
                
                <h4 className="font-semibold text-slate-900 mb-4">Municipal metering applications:</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: Droplets, text: 'Water networks' },
                    { icon: Flame, text: 'District heating' },
                    { icon: Building2, text: 'Municipal buildings' },
                    { icon: Landmark, text: 'Public utilities' },
                    { icon: Zap, text: 'Street lighting' },
                    { icon: Gauge, text: 'Environmental monitoring' },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <Icon className="h-5 w-5 text-teal-600" />
                        <span className="text-slate-700">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="bg-teal-600 p-8 rounded-lg text-white">
                <h3 className="text-xl font-semibold mb-6">Learn more about smart city infrastructure</h3>
                <p className="text-teal-100 mb-6">
                  0g Baltics supports municipalities with comprehensive IoT infrastructure that extends beyond metering to include environmental monitoring, asset tracking, and public safety applications.
                </p>
                <Link 
                  to="/iot-for-cities"
                  className="inline-flex items-center bg-white text-teal-700 px-6 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-colors"
                >
                  IoT for Cities
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHY 0G BALTICS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="why-0g-baltics">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  Your Partner
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  Why Deploy Smart Metering with 0g Baltics
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  0g Baltics provides the infrastructure foundation for successful smart metering projects. Our approach focuses on long-term network reliability and scalable infrastructure that utilities can depend on for decades.
                </p>
                
                <div className="space-y-4 mb-8">
                  {[
                    { title: 'Carrier-grade IoT infrastructure', desc: 'Networks designed for telecom-level reliability and availability' },
                    { title: 'LoRaWAN network deployment', desc: 'Professional network design, installation, and management' },
                    { title: 'Multi-connectivity architecture', desc: 'Hybrid networks combining LoRaWAN, NB-IoT, and cellular' },
                    { title: 'IoT device integration', desc: 'Support for meters from leading manufacturers' },
                    { title: 'Smart city infrastructure support', desc: 'Unified networks for metering and beyond' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold text-slate-900">{item.title}</h4>
                        <p className="text-slate-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="bg-slate-900 p-6 rounded-lg">
                  <p className="text-slate-300 italic">
                    "Our approach focuses on <strong className="text-white">long-term network reliability</strong> and <strong className="text-white">scalable infrastructure</strong>—not short-term solutions that fail when utilities need them most."
                  </p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-slate-50 p-8 rounded-lg border border-slate-200">
                  <h4 className="font-semibold text-slate-900 mb-6 text-lg">Related Solutions from 0g Baltics</h4>
                  <div className="space-y-4">
                    {[
                      { to: '/connectivity', label: 'Multi-Network Connectivity', desc: 'LoRaWAN, NB-IoT, Sigfox solutions' },
                      { to: '/tektelic', label: 'TEKTELIC Infrastructure', desc: 'Carrier-grade LoRaWAN gateways' },
                      { to: '/iot-for-cities', label: 'IoT for Smart Cities', desc: 'Municipal IoT deployments' },
                    ].map((link, i) => (
                      <Link key={i} to={link.to} className="block bg-white border border-slate-200 p-4 rounded-lg hover:border-teal-500 transition-colors group">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="font-medium text-slate-900">{link.label}</h5>
                            <p className="text-slate-600 text-sm">{link.desc}</p>
                          </div>
                          <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* CTA SECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-teal-600" data-testid="cta-section">
          <div className="container-0g text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('smartMeteringPage.ctaTitle')}
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
              {t('smartMeteringPage.ctaDesc')}
            </p>
            <Button 
              size="lg"
              className="bg-white text-teal-700 hover:bg-teal-50 font-semibold"
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="cta-button"
            >
              {t('smartMeteringPage.ctaButton')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* ========================================= */}
        {/* CONTACT FORM */}
        {/* ========================================= */}
        <div id="contact-form">
          <ContactForm />
        </div>

      </main>

      <Footer />
    </>
  );
};

export default SmartMeteringPage;
